'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faMoneyCheckDollarPen } from '@fortawesome/pro-solid-svg-icons';
import {
  Box,
  Card,
  CardContent,
  CodeChip,
  Typography,
  Tooltip,
  ProfileCardHeader,
} from 'src/components';
import { useProfile } from 'src/context/providers/ProfileProvider';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import { useColumnResizing } from 'src/hooks/table/useColumnResizing';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import {
  ReimbursementAccordionRow,
  ReimbursementHeader,
  ReimbursementRow,
  NoReimbursementData,
} from './components';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { titleCase } from 'src/utils/titleCase/titleCase';
import { REIMBURSEMENT_DATA_YEAR_STR } from 'src/components/constants';
interface TableHeader {
  label: string;
  key: string;
  width?: number;
  minWidth?: number;
  isSortable: boolean;
  isResizable: boolean;
  hasTooltip?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipLabel?: string;
}

const tableHeaders: TableHeader[] = [
  {
    label: 'Payer Name',
    key: 'payer',
    width: 250,
    minWidth: 150,
    isSortable: true,
    isResizable: true,
  },
  {
    label: 'Site of Care',
    key: 'siteOfCare',
    width: 175,
    minWidth: 175,
    isSortable: true,
    isResizable: true,
  },
  {
    label: 'Avg. Reimbursement',
    key: 'reimbursement',
    isSortable: true,
    isResizable: false,
    hasTooltip: true,
    tooltipLabel: 'What is "Average Reimbursement"?',
    tooltipContent:
      'The average reimbursement for this code, from this payer to this specific provider, at this site of care type.',
  },
  {
    label: 'State Average',
    key: 'stateAverage',
    isSortable: true,
    isResizable: false,
    hasTooltip: true,
    tooltipLabel: 'What is "State Average"?',
    tooltipContent:
      'The average reimbursement for this code, from this payer, across all providers in the state at this site of care type.',
  },
  {
    label: 'vs. State Avg. ($)',
    key: 'stateAvg',
    isSortable: true,
    isResizable: false,
    hasTooltip: true,
    tooltipLabel: 'What is "vs. State Average"?',
    tooltipContent:
      'The difference between this specific provider’s average and the state average for this code, payer, and site of care type.',
  },
  {
    label: 'Total Claims',
    key: 'totalClaims',
    isSortable: true,
    isResizable: false,
  },
];

// A payer has nothing to show when it has neither a provider average nor a
// state average. Treats null/undefined and 0 alike (the API returns null for
// payers with no reimbursement data).
const payerHasNoReimbursement = (payer: {
  avg_reimbursement_predicted?: number | null;
  state_avg_reimbursement_predicted?: number | null;
}) =>
  !payer.avg_reimbursement_predicted &&
  !payer.state_avg_reimbursement_predicted;

const ProfileReimbursementTable = () => {
  const { type } = useParams<{ type: string }>();
  const searchParams = useSearchParams();
  const code = searchParams.get('code');
  const payer_type = searchParams.get('payer_type');
  const payer_name = searchParams.get('payer_name');

  const { entityDetails } = useProfile();
  const { selectedFilterSet } = useProfileSelections(type);

  const { data: reimbursementData } = useGetPayerMixReimbursement({
    providerId: entityDetails?.provider_id,
    location: type,
  });
  const isProvider =
    entityDetails?.type === 'hcp' || entityDetails?.content_type === 'hcp';
  const subHeader = isProvider
    ? `Includes only professional fees data from ${REIMBURSEMENT_DATA_YEAR_STR}; Indicates the allowed amount on a claim, prior to the application of any patient cost-sharing.`
    : `Includes only institutional fees data from ${REIMBURSEMENT_DATA_YEAR_STR}; Indicates the allowed amount on a claim, prior to the application of any patient cost-sharing.`;

  // Initialize with default column widths
  const { startResizing, getAdjustedWidth } = useColumnResizing({
    innerColumnOffset: 24,
    defaultWidths: tableHeaders
      .filter((header) => header.isResizable)
      .map((header) => ({
        key: header.key,
        width: header.width,
        minWidth: header.minWidth,
      })),
  });

  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('percentOfTotal');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const groupedData = useMemo(() => {
    // Group by code first, then by payer type - no sorting here
    return reimbursementData?.results?.reduce((acc, item) => {
      if (!acc[item.code_name]) {
        acc[item.code_name] = {};
      }
      item.payers.forEach((payer) => {
        if (!acc[item.code_name][payer.payer_type]) {
          acc[item.code_name][payer.payer_type] = [];
        }
        acc[item.code_name][payer.payer_type].push(payer);
      });
      return acc;
    }, {} as Record<string, Record<string, typeof reimbursementData.results[0]['payers']>>);
  }, [reimbursementData]);

  const { firstCode, firstPayerType, firstPayer } = useMemo(() => {
    // Early return if groupedData is null/undefined
    if (!groupedData || typeof groupedData !== 'object') {
      return {
        firstCode: null,
        firstPayerType: null,
        firstPayer: null,
      };
    }

    const codes = Object.keys(groupedData);
    if (!codes.length) {
      return {
        firstCode: null,
        firstPayerType: null,
        firstPayer: null,
      };
    }

    const firstCode = codes[0];
    const firstCodeData = groupedData[firstCode];

    if (!firstCodeData || typeof firstCodeData !== 'object') {
      return {
        firstCode,
        firstPayerType: null,
        firstPayer: null,
      };
    }

    const payerTypes = Object.keys(firstCodeData);
    if (!payerTypes.length) {
      return {
        firstCode,
        firstPayerType: null,
        firstPayer: null,
      };
    }

    const firstPayerType = payerTypes[0];
    const firstPayerData = firstCodeData[firstPayerType];

    return {
      firstCode,
      firstPayerType,
      firstPayer: Array.isArray(firstPayerData)
        ? firstPayerData[0] ?? null
        : null,
    };
  }, [groupedData]);

  // Effect to handle initial scroll and expand
  useEffect(() => {
    if (code && groupedData?.[code]) {
      setExpandedCode(code);
      if (payer_type && payer_name) {
        setExpandedType(payer_type);
      }

      // Scroll into view with a small delay to ensure rendering is complete
      setTimeout(() => {
        if (payer_type && payer_name) {
          const idName = payer_name?.replace(/ /g, '-')?.toLowerCase();
          // if payer_type and payer_name are set, scroll to the payer row
          const element = document.getElementById(
            `reimbursement-table-payer-row-${payer_type}-${idName}`
          );
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // if payer_type and payer_name are not set, scroll to the code row
          const element = document.getElementById(
            `reimbursement-table-code-row-${code}`
          );
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else if (firstCode && firstPayerType && firstPayer) {
      setExpandedCode(firstCode);
      setExpandedType(firstPayerType);
    }
  }, [
    code,
    groupedData,
    payer_type,
    payer_name,
    firstCode,
    firstPayerType,
    firstPayer,
  ]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const getSortedPayers = (
    payers: typeof reimbursementData.results[0]['payers']
  ) => {
    return [...payers].sort((a, b) => {
      const multiplier = sortOrder === 'asc' ? 1 : -1;

      switch (sortColumn) {
        case 'payer':
          return multiplier * a.payer_name.localeCompare(b.payer_name);
        case 'siteOfCare':
          return (
            multiplier * (a.soc_type || '').localeCompare(b.soc_type || '')
          );
        case 'reimbursement':
          return (
            multiplier *
            ((a.avg_reimbursement_predicted ?? 0) -
              (b.avg_reimbursement_predicted ?? 0))
          );
        case 'stateAverage':
          return (
            multiplier *
            ((a.state_avg_reimbursement_predicted ?? 0) -
              (b.state_avg_reimbursement_predicted ?? 0))
          );
        case 'stateAvg':
          return (
            multiplier *
            (Math.abs(
              (a.avg_reimbursement_predicted ?? 0) -
                (a.state_avg_reimbursement_predicted ?? 0)
            ) -
              Math.abs(
                (b.avg_reimbursement_predicted ?? 0) -
                  (b.state_avg_reimbursement_predicted ?? 0)
              ))
          );
        case 'totalClaims':
          // Handle special case for string values like "<11"
          if (
            typeof a.claims_total === 'string' ||
            typeof b.claims_total === 'string'
          ) {
            // If sorting ascending, "<11" should be at the top (treated as lowest)
            // If sorting descending, "<11" should be at the bottom (treated as highest)
            if (
              typeof a.claims_total === 'string' &&
              a.claims_total.startsWith('<')
            ) {
              return sortOrder === 'asc' ? -1 : 1; // Push to top for asc, bottom for desc
            }
            if (
              typeof b.claims_total === 'string' &&
              b.claims_total.startsWith('<')
            ) {
              return sortOrder === 'asc' ? 1 : -1; // Push to top for asc, bottom for desc
            }
          }
          // Default numeric comparison for regular numbers
          return multiplier * (a.claims_total - b.claims_total);
        default:
          return 0;
      }
    });
  };

  const toggleAccordion = ({
    value,
    type,
  }: {
    value: string;
    type: string;
  }) => {
    switch (type) {
      case 'code': {
        const newExpandedCode = expandedCode === value ? null : value;
        setExpandedCode(newExpandedCode);
        if (!newExpandedCode) {
          setExpandedType(null);
        }
        break;
      }
      case 'type': {
        const newExpandedType = expandedType === value ? null : value;
        setExpandedType(newExpandedType);
        break;
      }
      default:
        break;
    }
  };

  const currentValue = useMemo(() => {
    const hasValue = selectedFilterSet;
    if (!hasValue) return '- All Claims';

    const name = selectedFilterSet?.label;
    return `- ${name} Claims`;
  }, [selectedFilterSet]);

  const hasSelectedValue = useMemo(() => {
    return !!selectedFilterSet;
  }, [selectedFilterSet]);

  const noPayerData = useMemo(() => {
    if (!reimbursementData?.results?.length) return true;
    const allPayersHaveZeroReimbursement = reimbursementData.results.every(
      (item) => item.payers.every(payerHasNoReimbursement)
    );
    return (
      reimbursementData.results.length === 0 || allPayersHaveZeroReimbursement
    );
  }, [reimbursementData]);

  return (
    <Card
      id="payer-mix-reimbursement"
      className="flex flex-col gap-4 p-6 min-h-64 bg-white border border-neutral-200 rounded-lg shadow-sm"
    >
      <ProfileCardHeader
        icon={faMoneyCheckDollarPen}
        title="Contracted Reimbursement"
        hasSelectedSavedSearch={hasSelectedValue}
        currentSavedSearch={currentValue}
        subHeader={subHeader}
        isBeta={true}
      />
      <CardContent className="h-full">
        {reimbursementData?.results?.length > 0 ? (
          <div className="flex flex-col gap-2">
            {/* Table Header Row */}
            <div className="w-full flex pl-16 pr-4 gap-2">
              {tableHeaders.map((header) => (
                <ReimbursementHeader
                  key={header.key}
                  column={header}
                  onResizing={startResizing}
                  isSortable={header.isSortable}
                  isResizable={header.isResizable}
                  onSort={handleSort}
                  sortOrder={sortOrder}
                  isSorting={sortColumn === header.key}
                  sx={
                    header.isResizable
                      ? getAdjustedWidth({
                          columnId: header.key,
                        })
                      : {}
                  }
                >
                  {header.hasTooltip && header.tooltipContent && (
                    <Tooltip
                      content={
                        <Box className="flex flex-col gap-1 p-1">
                          <Typography
                            className="font-semibold text-xs"
                            color="inherit"
                          >
                            {header.tooltipLabel}
                          </Typography>
                          <Typography className="text-xs" color="inherit">
                            {header.tooltipContent}
                          </Typography>
                        </Box>
                      }
                      position="top"
                      slotProps={{
                        containerClassName: 'max-w-72 whitespace-normal ',
                      }}
                    >
                      <span className="inline-flex">
                        <Icon
                          icon={faCircleQuestion}
                          size="sm"
                          className="text-neutral-600 cursor-pointer"
                        />
                      </span>
                    </Tooltip>
                  )}
                </ReimbursementHeader>
              ))}
            </div>
            {/* Code Table */}
            <div className="flex flex-col gap-2">
              {Object.entries(groupedData ?? {}).map(([code, payerTypes]) => {
                return (
                  <div
                    key={code}
                    className="bg-neutral-050 rounded-lg overflow-hidden"
                  >
                    <ReimbursementAccordionRow
                      id={`reimbursement-table-code-row-${code}`}
                      isExpanded={expandedCode === code}
                      onExpand={() =>
                        toggleAccordion({ value: code, type: 'code' })
                      }
                    >
                      {/* <CodeChipWDesc name={code} /> */}
                      <CodeChip code={code} />
                    </ReimbursementAccordionRow>

                    {expandedCode === code && (
                      // Payer Table
                      <div className="flex flex-col animate-fade-in pt-0 pb-4 px-6">
                        <div className="flex flex-col gap-2 px-4 py-2">
                          {Object.entries(payerTypes ?? {}).map(
                            ([type, payers]) => (
                              <div key={type} className="flex flex-col gap-2">
                                <ReimbursementAccordionRow
                                  id={`reimbursement-table-type-row-${type}`}
                                  isExpanded={expandedType === type}
                                  onExpand={() =>
                                    toggleAccordion({
                                      value: type,
                                      type: 'type',
                                    })
                                  }
                                  className="!px-0 "
                                >
                                  <div className="inline-flex items-baseline">
                                    {type}
                                    {payers.every(payerHasNoReimbursement) && (
                                      <Typography className="text-xs !leading-3 text-tertiary font-light ml-2">
                                        (No reimbursement information available)
                                      </Typography>
                                    )}
                                  </div>
                                </ReimbursementAccordionRow>
                                {expandedType === type && (
                                  <div className="flex flex-col pl-8 animate-fade-in">
                                    {payers.every(payerHasNoReimbursement) ? (
                                      <NoReimbursementData allZero={false} />
                                    ) : (
                                      getSortedPayers(payers)?.map((payer) => {
                                        const name = payer.payer_name;
                                        const type = titleCase(
                                          payer.payer_type
                                        );
                                        const isSelected =
                                          type === payer_type &&
                                          name === payer_name;

                                        return (
                                          <ReimbursementRow
                                            key={`${payer.payer_name}-${payer.soc_type}`}
                                            payer={payer}
                                            isSelected={isSelected}
                                            onResize={getAdjustedWidth}
                                          />
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full min-h-32">
            {hasSelectedValue && noPayerData ? (
              <NoReimbursementData allZero={noPayerData} />
            ) : (
              <p className="text-sm font-semibold text-neutral-500">
                Select a Code Set to see results
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileReimbursementTable;
